import React, {createContext, useContext, useState, useEffect} from "react";
import {ThemeProvider} from "@mui/material/styles";
import {tenantThemes, getThemeConfig, TenantTheme} from "./tenantThemes";

interface TenantThemeContextProps {
  tenant: string;
  setTenant: (tenant: string) => void;
  themeConfig: TenantTheme;
  tenantLogo?: string;
  tenantFavicon?: string;
}

const TenantThemeContext = createContext<TenantThemeContextProps>({
  tenant: "default",
  setTenant: () => {},
  themeConfig: tenantThemes.default,
});

export const useTenantTheme = () => useContext(TenantThemeContext);

export const TenantThemeProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [tenant, setTenant] = useState("default");
  const [tenantLogo, setTenantLogo] = useState<string | undefined>();
  const [tenantFavicon, setTenantFavicon] = useState<string | undefined>();
  const themeConfig = getThemeConfig(tenant);

  // Update favicon when tenant changes
  useEffect(() => {
    const favicon = tenantFavicon || themeConfig.siteIcon;
    if (favicon) {
      const link = (document.querySelector("link[rel*='icon']") as HTMLLinkElement) || document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      link.href = favicon;
      document.getElementsByTagName("head")[0].appendChild(link);
    }
  }, [tenantFavicon, themeConfig.siteIcon]);

  // Update document title and favicon when theme changes
  useEffect(() => {
    // Update favicon
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon) {
      favicon.href = themeConfig.siteIcon;
    } else {
      const newFavicon = document.createElement("link");
      newFavicon.rel = "icon";
      newFavicon.href = themeConfig.siteIcon;
      document.head.appendChild(newFavicon);
    }

    // Update document title with theme name
    const originalTitle = document.title.replace(/ - .*$/, "");
    document.title = `${originalTitle} - ${themeConfig.name}`;
  }, [themeConfig]);

  const contextValue = {
    tenant,
    setTenant,
    themeConfig,
    tenantLogo,
    tenantFavicon,
  };

  return (
    <TenantThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={themeConfig.theme}>{children}</ThemeProvider>
    </TenantThemeContext.Provider>
  );
};
